// src/Views/CartView.js
import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_HERO,
} from '../constants/layout';
import { formatPrice } from '../utils/priceFormatter';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import ContentUnavailableView from './ContentUnavailableView';
import { buildProductPath } from '../utils/productSlug';
import { AUTH_ACTION_LABEL } from '../constants/settings';
import {
  stripeConfigurationError,
  stripeConfiguredMode,
} from '../contexts/StripeContext';
import { calculateCartLineMinor } from '../utils/catalogMoney';
import { paymentV2StoreGateAllows } from '../utils/paymentV2StoreGate';

function CartView() {
  const {
    items,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartSubtotal,
    cartTax,
    estimatedTotal,
    cartPricingValid,
    cartLoading,
    cartReady,
    cartPersistenceError,
    products,
    getCoverImage,
  } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { store, loading: storeLoading } = useContext(StoreContext);
  const { showPrices } = useDisplayPreferences();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderError, setOrderError] = useState(null);

  const paymentGateReady = !storeLoading &&
    paymentV2StoreGateAllows(store) &&
    !stripeConfigurationError;
  const checkoutAllowed = Boolean(user && paymentGateReady && cartPricingValid);

  // Currency comes from the store, not individual products
  const currency = store?.currency || 'USD';

  // Auto-remove deactivated items from the cart
  useEffect(() => {
    if (!cartReady) return;
    items.forEach(item => {
      const product = products.find(p => p.id === item.productID);
      if (product?.deactivatedDate != null) {
        removeFromCart(item.id);
      }
    });
  }, [cartReady, items, products, removeFromCart]);

  const handleCheckout = () => {
    if (!user || items.length === 0 || !paymentGateReady || !cartPricingValid) return;
    navigate('/checkout');
  };

  if (cartPersistenceError) {
    return (
      <ContentUnavailableView
        icon="error_outline"
        title="Your Cart Is Unavailable"
        description="Your cart could not be safely loaded. Reload this page to retry."
        actions={[{ label: 'Continue Shopping', to: '/' }]}
      />
    );
  }

  if (!cartReady) {
    return (
      <ContentUnavailableView
        icon="shopping_cart"
        title="Loading Your Cart"
        description="Restoring your saved items..."
        actions={[]}
      />
    );
  }

  // Check if cart is empty
  if (items.length === 0) {
    return (
      <ContentUnavailableView
        icon="shopping_cart"
        title="Your Cart Is Empty"
        description="You haven't added any items to your cart yet."
        actions={[{ label: 'Continue Shopping', to: '/' }]}
      />
    );
  }

  return (
    <div className="cart-container">
      <div className="cart-header">
        <h1 className="section-title">Cart</h1>
      </div>

      <div className="cart-items-section">
        {items.map(item => {
          // Look up product data
          const product = products.find(p => p.id === item.productID);
          if (!product) return null;

          // Get cover image (similar to iOS ImageItemsManager)
          const coverImage = getCoverImage(product);

          const linePricing = calculateCartLineMinor(item, product, store);
          const productCurrency = linePricing?.currency || store?.currency || 'USD';
          const unitPrice = linePricing?.unitMinor ?? null;
          const lineTotal = linePricing?.lineTotalMinor ?? null;

          return (
            <div key={item.id} className="cart-item">
              <div className="item-content">
                {coverImage?.imageURL ? (
                  <div className="item-image-wrapper">
                    <img
                      src={coverImage.thumbnailURL || coverImage.imageURL}
                      alt={product.productName}
                      className="item-image"
                    />
                  </div>
                ) : (
                  <div className="item-image-wrapper">
                    <div className="product-placeholder">
                      <span className="material-icons placeholder-icon">photo</span>
                    </div>
                  </div>
                )}

                <div className="item-details">
                  <Link to={buildProductPath(product)} className="item-name">
                    {product.productName}
                  </Link>

                  {item.selectedModifiers && item.selectedModifiers.length > 0 && (
                    <div className="item-modifiers">
                      {item.selectedModifiers.map((mod, idx) => {
                        const group = product.modifierGroups?.find(g => g.id === mod.groupID);
                        const option = group?.modifierOptions?.find(o => o.id === mod.optionID);
                        return (
                          <span key={idx} className="modifier-text">
                            {option?.title || 'Unknown modifier'}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="item-meta">
                    <span className="meta-text">
                      Quantity {item.quantity}
                    </span>
                    {showPrices && (
                      <>
                        <span className="meta-separator"> | </span>
                        <span className="meta-text">
                          Item Price {formatPrice(unitPrice, productCurrency)}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="item-actions">
                    <div className="quantity-controls">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="quantity-button"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="quantity-display">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="quantity-button"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="remove-button"
                      aria-label="Remove item from cart"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="trash-icon"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>

                {showPrices && (
                  <div className="item-total">
                    {formatPrice(lineTotal, productCurrency)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="cart-summary">
        <div className="summary-card">
          {showPrices && (
            cartLoading ? (
              <div style={{ textAlign: 'center', padding: '10px 0', color: '#999' }}>
                Loading prices...
              </div>
            ) : !cartPricingValid ? (
              <div style={{ textAlign: 'center', padding: '10px 0', color: '#b42318' }}>
                Pricing is unavailable until the catalog payment schema is verified.
              </div>
            ) : (
              <>
                <div className="summary-row">
                  <span className="summary-label">Bag Subtotal</span>
                  <span className="summary-value">{formatPrice(cartSubtotal, currency)}</span>
                </div>

                <div className="summary-row">
                  <span className="summary-label">Estimated Tax</span>
                  <span className="summary-value">{formatPrice(cartTax, currency)}</span>
                </div>

                <div className="summary-row total-row">
                  <span className="summary-label">Order Total</span>
                  <span className="summary-value">{formatPrice(estimatedTotal, currency)}</span>
                </div>
              </>
            )
          )}

          {store?.isAcceptingOrders === false && (
            <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 10px' }}>
              Not accepting orders right now
            </p>
          )}

          {user ? (
            <>
              <button
                className="checkout-button"
                onClick={handleCheckout}
                disabled={isSubmitting || !checkoutAllowed}
              >
                {'Check Out'}
              </button>
              {(!paymentGateReady || !cartPricingValid) && (
                <div className="checkout-coming-soon">
                  <span className="material-icons coming-soon-icon">info</span>
                  <span>Secure checkout is currently unavailable.</span>
                </div>
              )}
              {checkoutAllowed && stripeConfiguredMode === 'test' && (
                <div className="checkout-sandbox-notice">
                  <span className="material-icons sandbox-icon">warning</span>
                  <span>This is a test sandbox. You will not be charged.</span>
                </div>
              )}
            </>
          ) : (
            <Link to="/login" className="checkout-button sign-in-link">
              {AUTH_ACTION_LABEL} to Check Out
            </Link>
          )}

          {orderError && (
            <p className="checkout-error">{orderError}</p>
          )}
        </div>
      </div>

      <style jsx>{`
        .cart-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_CARD_BODY} ${SPACING_BUTTON} ${SPACING_SECTION};
        }

        .cart-header {
          margin-bottom: ${SPACING_CARD};
        }

        .section-title {
          font-size: 1.75rem;
          font-family: var(--font-family-base);
          font-weight: 600;
          margin: 0;
          color: var(--color-text-primary);
        }

        .cart-items-section {
          margin-bottom: ${SPACING_CARD};
        }

        .cart-item {
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: ${SPACING_ELEMENT};
          padding: ${SPACING_CARD};
          margin-bottom: ${SPACING_BUTTON};
          overflow: hidden;
        }


        .item-content {
          display: flex;
          gap: ${SPACING_BUTTON};
        }

        .item-image-wrapper {
          flex-shrink: 0;
          width: 100px;
          height: 100px;
          overflow: hidden;
          background-color: var(--color-surface-muted);
        }

        .item-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--color-border);
        }

        .placeholder-icon {
          font-size: 32px;
          opacity: 0.5;
        }

        .item-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: ${SPACING_ELEMENT};
        }

        .item-name {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .item-name:hover {
          color: var(--color-accent);
        }

        .item-modifiers {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .modifier-text {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .item-meta {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .meta-separator {
          margin: 0 4px;
        }

        .item-actions {
          display: flex;
          align-items: center;
          gap: ${SPACING_BUTTON};
          margin-top: ${SPACING_ELEMENT};
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: ${SPACING_ELEMENT};
          border: 1px solid var(--color-border);
          border-radius: ${SPACING_ELEMENT};
          padding: ${SPACING_TIGHT};
          background-color: var(--color-surface-muted);
        }

        .quantity-button {
          width: 28px;
          height: 28px;
          border: none;
          background-color: transparent;
          color: var(--color-text-primary);
          font-size: 18px;
          cursor: pointer;
          border-radius: 4px;
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quantity-button:hover {
          background-color: var(--color-border);
        }

        .quantity-display {
          min-width: 32px;
          text-align: center;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .remove-button {
          padding: ${SPACING_ELEMENT};
          background-color: transparent;
          border: none;
          color: var(--color-text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .remove-button:hover {
          color: #ef4444;
        }

        .trash-icon {
          width: 20px;
          height: 20px;
        }

        .item-total {
          flex-shrink: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .cart-summary {
          display: flex;
          justify-content: flex-end;
        }

        .summary-card {
          width: 100%;
          max-width: 400px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: ${SPACING_COMPONENT};
        }

        .summary-label {
          font-size: 1rem;
          color: var(--color-text-secondary);
        }

        .summary-value {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .total-row {
          padding-top: ${SPACING_COMPONENT};
          border-top: 2px solid var(--color-border);
          margin-top: ${SPACING_COMPONENT};
        }

        .total-row .summary-label,
        .total-row .summary-value {
          font-size: 1rem;
          font-weight: 600;
        }

        .checkout-button {
          width: 100%;
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: 999px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease;
          margin-top: ${SPACING_CARD};
        }

        .checkout-button:not(:disabled):hover {
          background-color: var(--color-accent-hover);
        }

        .checkout-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .sign-in-link {
          display: block;
          text-align: center;
          text-decoration: none;
        }

        .checkout-sandbox-notice {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPACT};
          font-size: 0.875rem;
          color: #92400e;
          background-color: #fef3c7;
          padding: ${SPACING_COMPONENT} ${SPACING_BUTTON};
          border-radius: ${BORDER_RADIUS};
          margin-top: ${SPACING_COMPONENT};
          margin-bottom: 0;
        }

        .sandbox-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .checkout-coming-soon {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPACT};
          font-size: 0.875rem;
          color: #ef4444;
          margin-top: ${SPACING_COMPONENT};
          margin-bottom: 0;
        }

        .coming-soon-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .checkout-error {
          text-align: center;
          font-size: 0.875rem;
          color: #ef4444;
          margin-top: ${SPACING_COMPONENT};
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .item-image-wrapper {
            width: 80px;
            height: 80px;
          }

          .item-total {
            text-align: left;
          }

          .cart-summary {
            justify-content: stretch;
          }

          .summary-card {
            max-width: 100%;
          }
        }

        @media (max-width: 600px) {
          .section-title {
            font-size: 1.75rem;
          }

          .cart-item {
            padding: ${SPACING_BUTTON};
          }
        }
      `}</style>
    </div>
  );
}

export default CartView;
